import { useEffect, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  cancelDelegation, decideLeave, decideProfileChange, delegateApprovals, loadApprovals, loadDecisions, loadDelegations,
  loadDirectory, loadEducationLevels, loadLetterRequests, loadMemberNames, loadProfileChanges, loadPendingOvertime, decideOvertime, loadLoans, loadExpenses, decideExpense, type ExpenseClaim, loadReviews, loadTrainingRecords,
  type Approval, type Decision, type Delegation, type ProfileChange, type OvertimeEntry,
} from "../data";
import { ProfileChangeView } from "../components/ProfileChangeView";
import type { DirectoryRow, Workspace } from "../types";

const iso = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
type Tab = "waiting" | "history" | "cover";

/** One inbox for every decision waiting for the signed-in user, what they decided, and cover while away. */
export function Approvals({ ws, onOpen, onGo }: { ws: Workspace; onOpen: (employmentId: string) => void; onGo: (page: string) => void }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>("waiting");
  const tabs: [Tab, StringKey][] = [["waiting", "apprWaiting"], ["history", "tabHistory"], ["cover", "apprCover"]];
  return (
    <div className="page">
      <div className="tabs" role="tablist">
        {tabs.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "tab current" : "tab"} onClick={() => setTab(k)}>
            {t(label)}
          </button>
        ))}
      </div>
      {tab === "waiting" && <Waiting ws={ws} onOpen={onOpen} onGo={onGo} />}
      {tab === "history" && <History onOpen={onOpen} />}
      {tab === "cover" && <Cover ws={ws} />}
    </div>
  );
}

function Waiting({ ws, onOpen, onGo }: { ws: Workspace; onOpen: (id: string) => void; onGo: (page: string) => void }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const can = (p: string) => ws.permissions.has(p);
  const showChanges = ws.enabled.has("self_service") && can("employees.manage");
  const showLetters = ws.enabled.has("letters") && can("letters.issue");
  const [rows, setRows] = useState<Approval[] | null>(null);
  const [changes, setChanges] = useState<ProfileChange[]>([]);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [edu, setEdu] = useState<{ code: string; name_en: string; name_ar: string }[]>([]);
  const [letters, setLetters] = useState(0);
  const [overtime, setOvertime] = useState<OvertimeEntry[]>([]);
  const [loanReqs, setLoanReqs] = useState(0);
  const [claims, setClaims] = useState<ExpenseClaim[]>([]);
  const [toReview, setToReview] = useState(0);
  const [trainReqs, setTrainReqs] = useState(0);
  const showExpenses = ws.enabled.has("expenses") && (can("expenses.manage") || ws.teamSize > 0);
  const showOvertime = ws.enabled.has("overtime") && (can("attendance.manage") || ws.teamSize > 0);
  const showLoans = ws.enabled.has("loans") && can("loans.manage");
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      setRows(ws.enabled.has("leave") ? await loadApprovals() : []);
      if (showChanges) setChanges((await loadProfileChanges(cid)).filter((c) => c.status === "pending"));
      if (showLetters) setLetters((await loadLetterRequests(cid)).filter((r) => r.status === "pending").length);
      if (showOvertime) setOvertime((await loadPendingOvertime(cid)).filter((o) => o.employment_id !== ws.membership.employment_id));
      if (showExpenses) setClaims((await loadExpenses(cid)).filter((x) => x.employment_id !== ws.membership.employment_id
        && ((x.status === "submitted") || (x.status === "manager_approved" && can("expenses.manage")))));
      if (ws.enabled.has("performance") && ws.membership.employment_id) setToReview((await loadReviews(cid)).filter((r) =>
        r.reviewer_employment === ws.membership.employment_id && !r.manager_submitted_at && r.status !== "acknowledged").length);
      if (ws.enabled.has("training") && can("training.manage")) setTrainReqs((await loadTrainingRecords(cid)).filter((r) => r.status === "requested").length);
      if (showLoans) setLoanReqs((await loadLoans(cid)).filter((l) => l.status === "requested" && l.employment_id !== ws.membership.employment_id).length);
    } catch (e) { setError(errText(e, t("errorGeneric"))); }
  };
  useEffect(() => {
    void refresh();
    if (showChanges || showOvertime || showExpenses) void loadDirectory(cid).then(setPeople);
    if (showChanges) void loadEducationLevels().then(setEdu);
  }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps

  async function act(id: string, fn: () => Promise<unknown>) {
    setBusy(id);
    setError(null);
    try { await fn(); await refresh(); } catch (err) { setError(errText(err, t("errorGeneric"))); } finally { setBusy(null); }
  }
  const person = (id: string) => people.find((p) => p.employment_id === id);
  const nothing = rows !== null && rows.length === 0 && changes.length === 0 && letters === 0 && overtime.length === 0 && loanReqs === 0 && claims.length === 0 && toReview === 0 && trainReqs === 0;
  const otIds = overtime.map((o) => o.id);

  return (
    <>
      {error && <p className="alert danger" role="alert">{error}</p>}
      {nothing && <section className="card"><p className="muted">{t("noApprovals")}</p></section>}
      {rows === null && <p className="muted">{t("loading")}</p>}
      {rows && rows.length > 0 && (
        <section className="card">
          <h2>{t("apprLeave")} <span className="badge info">{rows.length}</span></h2>
          <ul className="approval-list">
            {rows.map((a) => (
              <li key={a.request_id} className="approval">
                <div className="approval-main">
                  <button type="button" className="link strong" onClick={() => onOpen(a.employment_id)}>{pick(a.employee_name, a.employee_name_ar)}</button>
                  <span>{pick(a.leave_type_en, a.leave_type_ar)} · <span className="mono">{a.start_date}{a.end_date !== a.start_date ? ` → ${a.end_date}` : ""}</span> · <span className="mono">{a.days}</span> {t("daysUnit")}</span>
                  {a.note && <span className="muted small-note">“{a.note}”</span>}
                  <span className="badge info">{t(`stage_${a.stage}` as StringKey)}</span>
                </div>
                <div className="approval-actions">
                  <input id={`note-${a.request_id}`} placeholder={t("decisionNote")} aria-label={t("decisionNote")} value={notes[a.request_id] ?? ""}
                    onChange={(e) => setNotes({ ...notes, [a.request_id]: e.target.value })} />
                  <div className="row">
                    <button type="button" className="btn primary small" disabled={busy === a.request_id} onClick={() => act(a.request_id, () => decideLeave(a.request_id, true, notes[a.request_id]))}>{t("approve")}</button>
                    <button type="button" className="btn secondary small" disabled={busy === a.request_id} onClick={() => act(a.request_id, () => decideLeave(a.request_id, false, notes[a.request_id]))}>{t("reject")}</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {changes.length > 0 && (
        <section className="card">
          <h2>{t("apprDetails")} <span className="badge info">{changes.length}</span></h2>
          <ul className="approval-list">
            {changes.map((c) => {
              const p = person(c.employment_id);
              const own = c.employment_id === ws.membership.employment_id;
              return (
                <li key={c.id} className="approval">
                  <div className="approval-main">
                    <button type="button" className="link strong" onClick={() => onOpen(c.employment_id)}>{p ? pick(p.full_name, p.full_name_ar) : "—"}</button>
                    <ProfileChangeView change={c} edu={edu} />
                    {c.note && <span className="muted small-note">“{c.note}”</span>}
                  </div>
                  <div className="approval-actions">
                    <input placeholder={t("decisionNote")} aria-label={t("decisionNote")} value={notes[c.id] ?? ""}
                      onChange={(e) => setNotes({ ...notes, [c.id]: e.target.value })} />
                    <div className="row">
                      <button type="button" className="btn primary small" disabled={busy === c.id || own} title={own ? t("apprNotOwn") : undefined}
                        onClick={() => act(c.id, () => decideProfileChange(c.id, true, notes[c.id]))}>{t("approve")}</button>
                      <button type="button" className="btn secondary small" disabled={busy === c.id}
                        onClick={() => act(c.id, () => decideProfileChange(c.id, false, notes[c.id]))}>{t("reject")}</button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      {overtime.length > 0 && (
        <section className="card">
          <div className="row spread">
            <h2>{t("apprOvertime")} <span className="badge info">{overtime.length}</span></h2>
            <div className="row">
              <button type="button" className="btn primary small" disabled={busy === "ot"} onClick={() => act("ot", () => decideOvertime(otIds, true))}>{t("apprApproveAll")}</button>
              {can("attendance.manage") && <button type="button" className="btn secondary small" onClick={() => onGo("overtime")}>{t("apprOpenOvertime")}</button>}
            </div>
          </div>
          <ul className="req-list">
            {overtime.slice(0, 25).map((o) => {
              const p = person(o.employment_id);
              return (
                <li key={o.id}>
                  <span><button type="button" className="link strong" onClick={() => onOpen(o.employment_id)}>{p ? pick(p.full_name, p.full_name_ar) : "—"}</button> · <span className="mono">{o.work_date}</span> · {t(`ot_${o.kind}` as StringKey)} · <span className="mono">{Math.floor(o.minutes / 60)}:{String(o.minutes % 60).padStart(2, "0")}</span></span>
                  <span className="row">
                    <button type="button" className="btn primary small" disabled={busy === o.id} onClick={() => act(o.id, () => decideOvertime([o.id], true))}>{t("approve")}</button>
                    <button type="button" className="btn secondary small" disabled={busy === o.id} onClick={() => act(o.id, () => decideOvertime([o.id], false))}>{t("reject")}</button>
                  </span>
                </li>
              );
            })}
          </ul>
          {overtime.length > 25 && <p className="muted small-note">{t("apprMoreOvertime").replace("{n}", String(overtime.length - 25))}</p>}
        </section>
      )}
      {claims.length > 0 && (
        <section className="card">
          <div className="row spread">
            <h2>{t("apprExpenses")} <span className="badge info">{claims.length}</span></h2>
            {can("expenses.manage") && <button type="button" className="btn secondary small" onClick={() => onGo("expenses")}>{t("apprOpenExpenses")}</button>}
          </div>
          <ul className="req-list">
            {claims.slice(0, 25).map((x) => {
              const p = person(x.employment_id);
              return (
                <li key={x.id}>
                  <span><button type="button" className="link strong" onClick={() => onOpen(x.employment_id)}>{p ? pick(p.full_name, p.full_name_ar) : "—"}</button> · {x.description} · <span className="mono">{Number(x.amount).toFixed(2)}</span>
                    <span className="muted small-note"> · {x.claim_date}{x.status === "manager_approved" ? ` · ${t("exStatus_manager_approved")}` : ""}</span></span>
                  <span className="row">
                    <button type="button" className="btn primary small" disabled={busy === x.id} onClick={() => act(x.id, () => decideExpense([x.id], true))}>{t("approve")}</button>
                    <button type="button" className="btn secondary small" disabled={busy === x.id} onClick={() => act(x.id, () => decideExpense([x.id], false))}>{t("reject")}</button>
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      {toReview > 0 && (
        <section className="card">
          <div className="row spread">
            <h2>{t("apprReviews")} <span className="badge info">{toReview}</span></h2>
            <button type="button" className="btn secondary small" onClick={() => onGo("performance")}>{t("apprOpenPerformance")}</button>
          </div>
        </section>
      )}
      {trainReqs > 0 && (
        <section className="card">
          <div className="row spread">
            <h2>{t("trRequests")} <span className="badge info">{trainReqs}</span></h2>
            <button type="button" className="btn secondary small" onClick={() => onGo("training")}>{t("apprOpenTraining")}</button>
          </div>
        </section>
      )}
      {loanReqs > 0 && (
        <section className="card">
          <div className="row spread">
            <h2>{t("loanRequests")} <span className="badge info">{loanReqs}</span></h2>
            <button type="button" className="btn secondary small" onClick={() => onGo("loans")}>{t("apprOpenLoans")}</button>
          </div>
        </section>
      )}
      {letters > 0 && (
        <section className="card">
          <div className="row spread">
            <h2>{t("apprLetters")} <span className="badge info">{letters}</span></h2>
            <button type="button" className="btn secondary small" onClick={() => onGo("letters")}>{t("apprOpenLetters")}</button>
          </div>
        </section>
      )}
    </>
  );
}

function History({ onOpen }: { onOpen: (id: string) => void }) {
  const { t, pick } = useI18n();
  const [rows, setRows] = useState<Decision[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { loadDecisions().then(setRows).catch((e) => setError(e.message)); }, []);
  return (
    <section className="card">
      <h2>{t("apprHistoryTitle")}</h2>
      {error && <p className="alert danger" role="alert">{error}</p>}
      {rows === null ? <p className="muted">{t("loading")}</p> : rows.length === 0 ? <p className="muted">{t("apprNoHistory")}</p> : (
        <div className="table-wrap"><table>
          <thead><tr><th>{t("employee")}</th><th>{t("leaveType")}</th><th className="hide-sm">{t("dates")}</th><th>{t("apprYourDecision")}</th><th className="hide-sm">{t("apprDecidedOn")}</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={`${r.request_id}-${r.step}`}>
              <td><button type="button" className="link" onClick={() => onOpen(r.employment_id)}>{pick(r.employee_name, r.employee_name_ar)}</button></td>
              <td>{pick(r.leave_type_en, r.leave_type_ar)}</td>
              <td className="hide-sm mono">{r.start_date}{r.end_date !== r.start_date ? ` → ${r.end_date}` : ""}</td>
              <td>
                <span className={`badge ${r.status === "rejected" ? "danger" : "success"}`}>
                  {r.step === "manager" && r.status === "pending" ? t("apprPassedOn") : t(`leaveStatus_${r.status}` as StringKey)}
                </span>
                {r.note && <span className="muted small-note"> “{r.note}”</span>}
              </td>
              <td className="hide-sm mono">{r.decided_at?.slice(0, 10)}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </section>
  );
}

function Cover({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const [list, setList] = useState<Delegation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => loadDelegations().then(setList).catch((e) => setError(e.message));
  useEffect(() => { void refresh(); }, []);
  return (
    <>
      {ws.membership.employment_id ? <Delegate ws={ws} onSaved={refresh} /> : <p className="alert info">{t("linkFirst")}</p>}
      <section className="card">
        <h2>{t("apprCoverList")}</h2>
        {error && <p className="alert danger" role="alert">{error}</p>}
        {list === null ? <p className="muted">{t("loading")}</p> : list.length === 0 ? <p className="muted">{t("apprNoCover")}</p> : (
          <ul className="plain-list">
            {list.map((d) => (
              <li key={d.id} className="row spread">
                <span>
                  {d.direction === "given" ? t("apprCoveredBy") : t("apprCoveringFor")} <strong>{d.other_name}</strong> · <span className="mono">{d.start_date} → {d.end_date}</span>
                  {d.active && <span className="badge success"> {t("active")}</span>}
                </span>
                {d.direction === "given" && (
                  <button type="button" className="btn secondary small" onClick={async () => {
                    try { await cancelDelegation(d.id); await refresh(); } catch (e) { setError(errText(e, t("errorGeneric"))); }
                  }}>{t("cancel")}</button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function Delegate({ ws, onSaved }: { ws: Workspace; onSaved: () => void }) {
  const { t } = useI18n();
  const [people, setPeople] = useState<{ user_id: string; display_name: string }[]>([]);
  const [f, setF] = useState({ to: "", start: iso(new Date()), end: iso(new Date(Date.now() + 7 * 864e5)) });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => { void loadMemberNames(ws.membership.company_id).then(setPeople); }, [ws.membership.company_id]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try { await delegateApprovals(f.to, f.start, f.end); setMsg({ ok: true, text: t("delegated") }); onSaved(); }
    catch (err) { setMsg({ ok: false, text: errText(err, t("errorGeneric")) }); }
  }

  return (
    <form className="card" onSubmit={save}>
      <h2>{t("delegateTitle")}</h2>
      <div className="field-row">
        <label className="field"><span>{t("delegateTo")}</span>
          <select id="dg-to" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} required>
            <option value="">—</option>
            {people.map((p) => <option key={p.user_id} value={p.user_id}>{p.display_name}</option>)}
          </select></label>
        <label className="field"><span>{t("from")}</span><input id="dg-start" type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} required /></label>
        <label className="field"><span>{t("to")}</span><input id="dg-end" type="date" min={f.start} value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} required /></label>
      </div>
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      <div className="actions"><button className="btn secondary" disabled={!f.to}>{t("delegateSave")}</button></div>
    </form>
  );
}
